import { assertError, type MaybeHTTPError } from '../errors';
import { type DebugLogger, type DebugLoggerFn, Logger } from '../logger';
import { getHeadersForTrace, getTraceFromHeaders } from './http';
import { generateSpanId, generateTraceId } from './ids';
import type { TraceState } from './types';

export interface TraceOptions {
    /**
     * If specified, the trace will be considered a root trace.
     * @default false
     */
    rootTrace?: boolean | string;

    /**
     * If specified, the trace will be created as a child of the given HTTP request trace.
     */
    httpHeaders?: {
        [key: string]: any;
    };

    /**
     * Sampling rate for this trace.
     */
    sampleRate?: number;

    /**
     * If silent, nothing will be logged. A trace can be silent and still have a non-zero sample rate.
     */
    silent?: boolean;
}

export interface TraceParentContext {
    traceId: string;
    parentSpanId: string | null;
}

let globalState: TraceState = {};

/**
 * Interface for a debugging trace.
 * In the backend, traces are tracked on Stackdriver and help debug performances.
 * In the browser, it can be shown by enabling the debug log "trace:"
 */
export class GenericTrace {
    /** ID of the span trace */
    public spanId: string;

    /** ID of the root trace associated */
    public traceId: string;

    /** ID of the parent span trace */
    public parentSpanId: string | null;

    /** Friendly name for the trace. */
    public name: string;

    /** Debugging labels associated with this trace. */
    public labels: { [key: string]: any } = {};

    /** State of the trace. It'll be propagated as headers. */
    public traceState: TraceState = {};

    /** Timestamp when the trace was started */
    public startTime: number | null;

    /** Timestamp when the trace was ended */
    public endTime: number | null;

    /** Logger to logs in the context of this trace. */
    public logger: DebugLogger;

    /** If silent, nothing will be logged. sampleRate will still be respected. You can set silent mid-way through a trace, it'll be respected at the end. */
    public silent: boolean;

    constructor(name: string, opts: TraceOptions = {}) {
        this.name = name;
        this.logger = Logger(`trace:${this.name}`);
        this.startTime = null;
        this.endTime = null;
        this.spanId = generateSpanId();
        this.silent = typeof opts.silent === 'undefined' ? false : opts.silent;

        const headers = opts.httpHeaders ? getTraceFromHeaders(opts.httpHeaders) : null;

        if (opts.rootTrace) {
            this.traceId = typeof opts.rootTrace === 'string' ? opts.rootTrace : generateTraceId();
            this.parentSpanId = null;
        } else if (headers) {
            this.traceId = headers.traceId;
            this.parentSpanId = headers.spanId;
            this.traceState = headers.state;
        } else {
            const parent = this.getParentContext();
            this.parentSpanId = parent.parentSpanId;
            this.traceId = parent.traceId;
        }
    }

    /**
     * Return the current current root trace.
     */
    public static getCurrentRootTrace(): GenericTrace | null {
        throw new Error('Not implemented');
    }

    /**
     * Return the current running span trace.
     */
    public static getCurrentSpanTrace(): GenericTrace | null {
        throw new Error('Not implemented');
    }

    /**
     * Define global state for all traces.
     */
    public static setGlobalState(state: TraceState) {
        globalState = {
            ...globalState,
            ...state,
        };
    }

    /**
     * Get the context (root trace and parent span) of the current trace.
     */
    public getParentContext(): TraceParentContext {
        throw new Error('Not implemented');
    }

    /**
     * Add a label on this trace.
     */
    public label(name: string, value: any) {
        this.labels[name] = value;
    }

    /**
     * Define multiple labels on this trace.
     */
    public setLabels(labels: Record<string, any>) {
        for (const [key, value] of Object.entries(labels)) {
            this.label(key, value);
        }
    }

    /**
     * Return the entire state of the trace.
     */
    public state(): TraceState {
        return {
            ...globalState,
            ...this.traceState,
        };
    }

    /**
     * Return the HTTP headers to propagate the trace.
     */
    public headers() {
        return getHeadersForTrace({
            spanId: this.spanId,
            traceId: this.traceId,
            state: this.state(),
        });
    }

    /**
     * Return true if the trace is a root trace.
     */
    public isRootTrace() {
        return !this.parentSpanId;
    }

    /**
     * Start the trace
     */
    protected start() {
        this.startTime = time();
    }

    /**
     * Finish the trace and record it.
     */
    protected end(error: MaybeHTTPError | null = null) {
        if (!this.startTime) {
            throw new Error('Ending a non-started trace');
        }

        this.endTime = time();
        const duration = this.endTime - this.startTime;

        // We don't log trace completion during unit tests as some assertions are expected to be failed (assertFailure)
        if (process.env.NODE_ENV !== 'test') {
            const labels = Object.keys(this.labels).length ? this.labels : undefined;

            /** Log a message with labels, protecting against undefined. */
            const logWithLabels = (fn: DebugLoggerFn, message: string) => {
                if (labels) {
                    fn.call(this.logger, message, labels);
                } else {
                    fn.call(this.logger, message);
                }
            };

            if (error) {
                const errorString =
                    process.env.NODE_ENV === 'development' ? error.stack : error.message;
                const message = `failed in ${duration.toFixed(2)}ms - ${errorString}`;

                if (error.expose) {
                    logWithLabels(this.logger.error, message);
                } else {
                    logWithLabels(this.logger.critical, message);
                }
            } else {
                const fn = this.silent ? this.logger.debug : this.logger.info;
                logWithLabels(fn, `succeeded in ${duration.toFixed(2)}ms`);
            }
        }
    }

    /**
     * Utility to wrap some async code, the wrapped function is provided a logger, which is labelled
     * with the trace labels.
     */
    public async wrap<T>(fn: () => Promise<T>): Promise<T> {
        try {
            this.start();
            const result = await fn();
            this.end();
            return result;
        } catch (error) {
            assertError(error);
            this.end(error);
            throw error;
        }
    }

    /**
     * Utility to wrap some sync code.
     */
    public wrapSync<T>(fn: () => T): T {
        try {
            this.start();
            const result = fn();
            this.end();
            return result;
        } catch (error) {
            assertError(error);
            this.end(error);
            throw error;
        }
    }
}

function time() {
    if (typeof performance !== 'undefined' && performance.now) {
        return performance.now();
    }

    return Date.now();
}
